import { Link } from 'react-router-dom';
import { CHURCH_CONFIG } from '../config';
import { CardIcon } from '../lib/icons';

const STARS = [
  { left: '8%', top: '15%', size: 2 }, { left: '18%', top: '8%', size: 1.5 }, { left: '30%', top: '20%', size: 1.5 },
  { left: '42%', top: '10%', size: 2 }, { left: '55%', top: '18%', size: 1.5 }, { left: '68%', top: '12%', size: 2 },
  { left: '80%', top: '22%', size: 1.5 }, { left: '92%', top: '9%', size: 1.5 }, { left: '15%', top: '32%', size: 1.5 },
  { left: '48%', top: '30%', size: 1.5 }, { left: '75%', top: '35%', size: 1.5 }, { left: '5%', top: '45%', size: 1.5 },
  { left: '88%', top: '42%', size: 1.5 }, { left: '25%', top: '5%', size: 1.5 }, { left: '60%', top: '6%', size: 1.5 },
];

function ServiceCard({ service }) {
  return (
    <div className="border border-gold-500/30 bg-earth-800/40 px-6 py-5 rounded text-center backdrop-blur-sm">
      <p className="font-display text-gold-400 text-xl">{service.day}</p>
      <p className="text-cream-100 text-2xl font-light mt-1">{service.time}</p>
      <p className="font-mono text-cream-200/60 text-xs uppercase tracking-wider mt-2">{service.label}</p>
    </div>
  );
}

function PointList({ text }) {
  return (
    <p
      className="text-cream-200/70 text-[11px] md:text-xs mt-1.5 leading-relaxed whitespace-nowrap overflow-hidden text-ellipsis"
      title={text}
    >
      {text}
    </p>
  );
}

function HeroTile({ label, title, icon, children }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl bg-earth-800/50 border border-gold-500/20 backdrop-blur-sm px-6 py-5 md:px-7 md:py-6 transition-all duration-300 ease-out hover:-translate-y-1 hover:border-gold-400/40 hover:bg-earth-800/70 hover:shadow-lg hover:shadow-black/20">
      <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-gold-500/10 blur-2xl pointer-events-none transition-all duration-300 group-hover:bg-gold-500/20 group-hover:scale-125" />
      <div className="relative flex items-start gap-4">
        <div className="w-11 h-11 shrink-0 rounded-full bg-gold-500/10 flex items-center justify-center transition-colors duration-300 group-hover:bg-gold-500/20">
          <CardIcon name={icon} className="w-5 h-5 text-gold-400 transition-transform duration-300 group-hover:scale-110" />
        </div>
        <div className="min-w-0">
          <p className="font-display leading-tight text-lg md:text-xl">
            <span className="text-gold-400/80">{label} </span>
            <span className="text-cream-50 font-semibold">{title}</span>
          </p>
          {children}
        </div>
      </div>
    </div>
  );
}

function NextStepCard({ icon, title, description, to, cta }) {
  return (
    <Link
      to={to}
      className="group bg-white border border-earth-800/10 rounded p-8 text-center hover:border-gold-400/50 hover:shadow-lg transition-all duration-200"
    >
      <div className="w-14 h-14 rounded-full bg-gold-500/10 flex items-center justify-center mx-auto mb-5 group-hover:bg-gold-500/20 transition-colors">
        <CardIcon name={icon} className="w-6 h-6 text-gold-600" />
      </div>
      <p className="font-display text-earth-800 text-xl mb-2">{title}</p>
      <p className="text-earth-700/60 text-sm leading-relaxed mb-4">{description}</p>
      <span className="text-gold-600 group-hover:text-gold-500 text-xs font-semibold uppercase tracking-wider">
        {cta} →
      </span>
    </Link>
  );
}

export default function Home() {
  return (
    <div className="font-body">
      {/* ── HERO ── */}
      <section
        className="relative flex items-center overflow-hidden py-28 lg:py-32 lg:min-h-screen"
        style={{ background: 'linear-gradient(160deg, #060e1e 0%, #0c2048 50%, #162d66 100%)' }}
      >
        {/* Starfield */}
        <div className="absolute inset-0 pointer-events-none">
          {STARS.map((s, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                left: s.left, top: s.top, width: s.size, height: s.size,
                background: 'rgba(255,255,240,0.8)',
                boxShadow: '0 0 3px 1px rgba(255,255,220,0.4)',
              }}
            />
          ))}
        </div>

        {/* Radiant glow, ambient behind the text column */}
        <div
          className="absolute left-1/4 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
          style={{ width: 620, height: 620, background: 'radial-gradient(circle, rgba(213,183,143,0.3) 0%, transparent 70%)' }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center w-full">
          <div className="text-center lg:text-left animate-fade-in">
            <p className="font-mono text-gold-400/80 uppercase tracking-wider text-xs mb-6 font-semibold">
              Welcome Home
            </p>
            <h1 className="font-display text-5xl md:text-6xl text-cream-50 leading-tight mb-6">
              {CHURCH_CONFIG.name}
            </h1>
            <p className="font-display italic text-gold-300 text-xl md:text-2xl mb-8 opacity-90">
              "{CHURCH_CONFIG.tagline}"
            </p>
            <p className="text-cream-200/70 max-w-xl mx-auto lg:mx-0 text-base leading-relaxed mb-10">
              {CHURCH_CONFIG.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
              <Link
                to="/about"
                className="bg-gold-500 hover:bg-gold-400 text-earth-900 font-semibold px-8 py-3 rounded-sm tracking-widest uppercase text-xs transition-colors duration-200"
              >
                Our Story
              </Link>
              <Link
                to="/support"
                className="border border-gold-500/60 hover:border-gold-400 text-gold-400 hover:text-gold-300 px-8 py-3 rounded-sm tracking-widest uppercase text-xs transition-colors duration-200"
              >
                Support Us
              </Link>
            </div>
          </div>

          {/* Vision / Mission / Philosophy / Culture tiles */}
          <div className="flex flex-col gap-4 animate-fade-in">
            <HeroTile label="Our" title="Vision" icon="book-open">
              <PointList text={CHURCH_CONFIG.about.vision} />
            </HeroTile>
            <HeroTile label="Our" title="Mission" icon="hand-helping">
              <PointList text={CHURCH_CONFIG.about.mission} />
            </HeroTile>
            <HeroTile label="Our" title="Philosophy" icon="hand-heart">
              <p className="text-cream-200/70 text-xs md:text-sm mt-1.5 leading-relaxed">
                {CHURCH_CONFIG.tagline}
              </p>
            </HeroTile>
            <HeroTile label="Our" title="Culture" icon="users">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1.5">
                {CHURCH_CONFIG.about.culture.map((c) => (
                  <p key={c} className="text-cream-200/70 text-xs md:text-sm">{c}</p>
                ))}
              </div>
            </HeroTile>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-8 inset-x-0 flex flex-col items-center gap-2 animate-bounce">
          <span className="w-px h-8 bg-gold-400/40 block" />
          <span className="text-gold-400/40 text-xs tracking-widest uppercase">Scroll</span>
        </div>
      </section>

      {/* ── WELCOME MESSAGE ── */}
      <section className="relative bg-cream-50 py-28 overflow-hidden">
        <p
          aria-hidden="true"
          className="absolute left-1/2 -translate-x-1/2 top-6 font-display text-gold-500/10 text-[220px] leading-none select-none pointer-events-none"
        >
          &ldquo;
        </p>

        <div className="relative max-w-2xl mx-auto px-6 text-center">
          <p className="font-mono text-gold-600 uppercase tracking-wider text-xs mb-3 font-semibold">Our Heart</p>
          <h2 className="font-display text-4xl md:text-5xl text-earth-800 mb-8 leading-snug">
            You Are Welcome Here
          </h2>
          <p className="text-earth-700/80 text-lg leading-relaxed mb-10">
            Whether you've been following Jesus your whole life or you're just beginning to ask the big questions,
            this is a place where you belong. Come as you are. You'll find a community ready to walk with you.
          </p>

          <Link
            to="/about"
            className="inline-block border-b-2 border-gold-500 text-earth-800 hover:text-gold-600 font-semibold tracking-wide text-sm transition-colors pb-0.5"
          >
            Learn More About Us →
          </Link>
        </div>
      </section>

      {/* ── SERVICE TIMES ── */}
      <section className="bg-earth-800 py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="font-mono text-gold-400/80 uppercase tracking-wider text-xs mb-3 font-semibold">Join Us</p>
          <h2 className="font-display text-3xl text-cream-100 mb-4">Service Schedule</h2>
          <p className="text-cream-200/60 max-w-xl mx-auto text-sm leading-relaxed mb-12">
            We'd love to have you. Join us for worship and prayer.
          </p>

          <div className="flex flex-wrap justify-center gap-5 mx-auto">
            {CHURCH_CONFIG.services.map((s, i) => (
              <div key={i} className="w-64">
                <ServiceCard service={s} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHERE TO FIND US ── */}
      <section className="bg-cream-100 py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="font-mono text-gold-600 uppercase tracking-wider text-xs mb-3 font-semibold">Plan Your Visit</p>
            <h2 className="font-display text-4xl text-earth-800">Where to Find Us</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto items-stretch">
            <div className="bg-white border border-gold-500/20 rounded-lg shadow-sm p-3 min-h-[280px]">
              <div className="w-full h-full rounded overflow-hidden">
                <iframe
                  title="Map to AGCC Marikina"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(CHURCH_CONFIG.address)}&output=embed`}
                  className="w-full h-full min-h-[260px] border-0 grayscale-[15%] contrast-[1.05]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <div className="bg-white border border-earth-800/10 rounded-lg shadow-sm p-8 text-center flex-1 flex flex-col justify-center">
                <div className="w-14 h-14 rounded-full bg-gold-500/10 flex items-center justify-center mx-auto mb-5">
                  <CardIcon name="map-pin" className="w-6 h-6 text-gold-600" />
                </div>
                <p className="font-display text-earth-800 text-lg mb-2">{CHURCH_CONFIG.address}</p>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CHURCH_CONFIG.address)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block mt-2 text-gold-600 hover:text-gold-500 text-xs font-semibold uppercase tracking-wider"
                >
                  Get Directions →
                </a>
              </div>

              <div className="bg-white border border-earth-800/10 rounded-lg shadow-sm p-8 text-center flex-1 flex flex-col justify-center">
                <div className="w-14 h-14 rounded-full bg-gold-500/10 flex items-center justify-center mx-auto mb-5">
                  <CardIcon name="shirt" className="w-6 h-6 text-gold-600" />
                </div>
                <p className="font-display text-earth-800 text-lg mb-2">Come As You Are</p>
                <p className="text-earth-700/60 text-sm leading-relaxed">
                  Casual dress and a warm welcome, no need to dress up.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES STRIP ── */}
      <section className="bg-earth-900 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gold-500/10">
            {CHURCH_CONFIG.about.values.map((v, i) => (
              <div key={i} className="bg-earth-900 p-8 text-center">
                <div className="w-10 h-10 rounded-full bg-gold-500/10 flex items-center justify-center mx-auto mb-3">
                  <CardIcon name={v.icon} className="w-5 h-5 text-gold-400" />
                </div>
                <p className="font-display text-gold-400 text-lg mb-2">{v.title}</p>
                <p className="text-cream-200/50 text-xs leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NEXT STEPS ── */}
      <section className="bg-cream-50 py-24 border-t border-earth-800/5">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="font-mono text-gold-600 uppercase tracking-wider text-xs mb-3 font-semibold">Get Involved</p>
            <h2 className="font-display text-4xl text-earth-800">Take Your Next Step</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            <NextStepCard
              icon="hand-coins"
              title="Give"
              description="Support the mission and ongoing ministries of the church."
              to="/support"
              cta="Give Now"
            />
            <NextStepCard
              icon="users"
              title="Serve"
              description="Use your gifts to serve alongside one of our ministry teams."
              to="/support?tab=volunteer"
              cta="Find a Ministry"
            />
            <NextStepCard
              icon="user-plus"
              title="Belong"
              description="Take the next step in your faith and become an official member."
              to="/membership"
              cta="Join Us"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
